import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { YouTubeSearchDialog } from './YouTubeSearchDialog';
import { sheetsService } from '../services/googleSheetsService';
import { youtubeService } from '../services/youtubeService';

interface VideoDetails {
  song: string;
  artist: string;
  thisWeek: number;
  lastWeek: number;
  weeks: number;
  peak: number;
  youtubeLink: string;
}

interface AddVideoDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (videos: VideoDetails[]) => void;
  videos: VideoDetails[];
}

export const AddVideoDetailsDialog: React.FC<AddVideoDetailsDialogProps> = ({
  open,
  onClose,
  onSave,
  videos,
}) => {
  const [videoDetails, setVideoDetails] = useState<VideoDetails[]>(videos);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchTargetIndex, setSearchTargetIndex] = useState<number | null>(null);
  const [searchInitialQuery, setSearchInitialQuery] = useState('');

  useEffect(() => {
    setVideoDetails(videos);
  }, [videos]);

  const handleSearchIconClick = (index: number) => {
    const video = videoDetails[index];
    const query = `${video.song} ${video.artist}`.trim();
    setSearchInitialQuery(query);
    setSearchTargetIndex(index);
    setSearchDialogOpen(true);
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
    setSearchInitialQuery('');
    setSearchTargetIndex(null);
  };

  const handleSearchDialogSelect = async (videoId: string) => {
    if (searchTargetIndex === null) return;
    
    const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;
    setLoading(true);
    setError(null);

    try {
      const officialSongInfo = await youtubeService.getSongInfo(fullUrl);
      
      const newDetails = [...videoDetails];
      newDetails[searchTargetIndex] = {
        ...newDetails[searchTargetIndex],
        youtubeLink: fullUrl,
        song: officialSongInfo.song,
        artist: officialSongInfo.artist,
      };
      setVideoDetails(newDetails);
    } catch (error) {
      console.error('Error fetching video info:', error);
    } finally {
      setLoading(false);
      handleSearchDialogClose();
    }
  };

  const handleYoutubeLinkChange = async (index: number, link: string) => {
    const newDetails = [...videoDetails];
    newDetails[index] = { ...newDetails[index], youtubeLink: link };
    setVideoDetails(newDetails);

    try {
      const videoId = extractYoutubeId(link);
      if (videoId) {
        setLoading(true);
        const videoInfo = await sheetsService.getVideoInfo(videoId);
        if (videoInfo) {
          newDetails[index] = {
            ...newDetails[index],
            song: videoInfo.song,
            artist: videoInfo.artist,
          };
          setVideoDetails(newDetails);
        }
      }
    } catch (error) {
      console.error('Error fetching video info:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractYoutubeId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleFieldChange = (index: number, field: keyof VideoDetails, value: string | number) => {
    const newDetails = [...videoDetails];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setVideoDetails(newDetails);
  };

  const handleSave = () => {
    onSave(videoDetails);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Video Details</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {videoDetails.map((video, index) => (
            <Box key={index} sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Song {index + 1}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Song"
                  value={video.song}
                  onChange={(e) => handleFieldChange(index, 'song', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Artist"
                  value={video.artist}
                  onChange={(e) => handleFieldChange(index, 'artist', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="This Week"
                  type="number"
                  value={video.thisWeek}
                  onChange={(e) => handleFieldChange(index, 'thisWeek', parseInt(e.target.value))}
                  fullWidth
                />
                <TextField
                  label="Last Week"
                  type="number"
                  value={video.lastWeek}
                  onChange={(e) => handleFieldChange(index, 'lastWeek', parseInt(e.target.value))}
                  fullWidth
                />
                <TextField
                  label="Peak"
                  type="number"
                  value={video.peak}
                  onChange={(e) => handleFieldChange(index, 'peak', parseInt(e.target.value))}
                  fullWidth
                />
                <TextField
                  label="Weeks on Chart"
                  type="number"
                  value={video.weeks}
                  onChange={(e) => handleFieldChange(index, 'weeks', parseInt(e.target.value))}
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="YouTube Link"
                    value={video.youtubeLink}
                    onChange={(e) => handleYoutubeLinkChange(index, e.target.value)}
                    fullWidth
                  />
                  <IconButton
                    onClick={() => handleSearchIconClick(index)}
                    title="Search YouTube"
                    sx={{ alignSelf: 'center' }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Save
        </Button>
      </DialogActions>

      <YouTubeSearchDialog
        open={searchDialogOpen}
        onClose={handleSearchDialogClose}
        onSelect={handleSearchDialogSelect}
        initialQuery={searchInitialQuery}
      />
    </Dialog>
  );
}; 